import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { User, Mail, Lock, Phone, ArrowRight, Check, Gift } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import SignatureCanvas from "react-signature-canvas";
import DietPlanUpsellModal from "../components/dashboard/DietPlanUpsellModal";
import { startMembershipCheckout } from "../utils/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MemberRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sigCanvas = useRef(null);
  
  const leadData = location.state?.leadData || {};
  const leadId = location.state?.leadId;
  const dollarOffer = location.state?.dollarOffer;
  const selectedPlan = location.state?.selectedPlan || "elite";
  const claimedFreeWeek = location.state?.claimedFreeWeek;
  const freeClass = location.state?.freeClass;

  // Purchase intent from URL params (homepage plan/offer clicks)
  const planIntent = searchParams.get('plan');       // e.g. "starter", "performance", "elite", "family_plan"
  const offerIntent = searchParams.get('offer');     // e.g. "firstclass", "freeweek"

  // Get referral code: URL param first, then localStorage
  const refCodeFromUrl = searchParams.get('ref');
  const refCodeFromStorage = localStorage.getItem('nbc_referral_code');
  const refCode = refCodeFromUrl || refCodeFromStorage || "";
  const [referrerName, setReferrerName] = useState("");

  const [step, setStep] = useState(1); // 1: registration, 2: waiver, 3: referral upsell (conditional)
  const [formData, setFormData] = useState({
    first_name: leadData.first_name || "",
    last_name: leadData.last_name || "",
    phone: leadData.phone || "",
    email: "",
    password: "",
    confirmPassword: "",
    referralCode: refCode
  });

  // Look up referrer name when code is present
  useEffect(() => {
    const lookupReferrer = async () => {
      if (formData.referralCode) {
        try {
          const response = await axios.get(`${API}/members/by-referral/${formData.referralCode}`);
          const name = response.data.first_name || response.data.name?.split(' ')[0] || "Someone";
          setReferrerName(name);
        } catch (error) {
          setReferrerName("");
        }
      } else {
        setReferrerName("");
      }
    };
    lookupReferrer();
  }, [formData.referralCode]);
  const [memberId, setMemberId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agreedToWaiver, setAgreedToWaiver] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [referralUpsellEnabled, setReferralUpsellEnabled] = useState(false);
  const [referrals, setReferrals] = useState([
    { first_name: "", last_name: "", phone: "" },
    { first_name: "", last_name: "", phone: "" },
    { first_name: "", last_name: "", phone: "" },
  ]);
  const [referralLoading, setReferralLoading] = useState(false);

  // Diet plan upsell — shown before checkout when member picked a paid plan from homepage
  const [dietUpsellData, setDietUpsellData] = useState(null); // { planId, planName, planPrice }
  const [registrationPlans, setRegistrationPlans] = useState([]);

  // Pre-fetch plans so we have name/price for the diet upsell modal
  useEffect(() => {
    const PAID_PLANS = ["starter", "performance", "elite", "family_plan"];
    if (planIntent && PAID_PLANS.includes(planIntent)) {
      axios.get(`${API}/plans/member-page`).then(r => setRegistrationPlans(r.data)).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planIntent]);

  // Fetch popup settings to check if Step 3 referral upsell is enabled
  useEffect(() => {
    axios.get(`${API}/popup-settings`)
      .then(res => setReferralUpsellEnabled(res.data?.referral_upsell_enabled === true))
      .catch(() => {}); // fail silently — defaults to false
  }, []);

  // Scroll to top on every step transition so the user sees the heading first
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.password || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        email: formData.email,
        password: formData.password,
        referral_code: formData.referralCode || null,
        trainer_referral_code: localStorage.getItem("nbc_trainer_referral_code") || null,
      });
      
      setMemberId(response.data.member.id);
      if (window.gtag) window.gtag('event', 'sign_up', { method: 'website' });
      if (window.ttq) window.ttq.track('CompleteRegistration');
      if (window.gtag) window.gtag('event', 'conversion', {'send_to': 'AW-380775216/HYImCIDa2qwdELDWyLUB'});
      localStorage.setItem("memberToken", response.data.token);
      localStorage.setItem("memberData", JSON.stringify(response.data.member));
      // Clear referral codes from storage — they've been used
      localStorage.removeItem("nbc_referral_code");
      localStorage.removeItem("nbc_trainer_referral_code");
      
      if (formData.referralCode && referrerName) {
        toast.success(`Referred by ${referrerName}! They'll get a free month.`);
      }
      
      toast.success("Account created! Please sign the waiver.");
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleWaiverSubmit = async () => {
    if (!agreedToWaiver) {
      toast.error("Please agree to the waiver terms");
      return;
    }
    
    if (sigCanvas.current?.isEmpty()) {
      toast.error("Please sign the waiver");
      return;
    }

    if (!dateOfBirth) {
      toast.error("Please enter your date of birth");
      return;
    }

    if (!homeAddress.trim()) {
      toast.error("Please enter your home address");
      return;
    }

    setLoading(true);
    try {
      const signatureData = sigCanvas.current.toDataURL();
      const token = localStorage.getItem("memberToken");
      
      await axios.post(
        `${API}/waivers`,
        {
          member_id: memberId,
          signature_data: signatureData,
          date_of_birth: dateOfBirth,
          home_address: homeAddress.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (window.fbq) window.fbq('track', 'CompleteRegistration', { content_name: 'NBC Member Account', status: true });
      toast.success("Waiver signed successfully!");
      
      const originUrl = window.location.origin;
      const currentMemberId = memberId;

      // ── Route based on purchase intent ──

      // 1. Dollar offer accepted (from lead popup $1 deal)
      if (dollarOffer) {
        const response = await axios.post(`${API}/payments/checkout`, {
          package_type: "dollar_promo",
          selected_plan: selectedPlan || "starter",  // pass chosen plan
          member_id: currentMemberId,
          origin_url: originUrl
        });
        window.location.href = response.data.url;
        return;
      }

      // 2. Specific membership plan clicked on homepage — show diet upsell first
      if (planIntent && ["starter", "performance", "elite", "family_plan"].includes(planIntent)) {
        const planObj = registrationPlans.find(p => p.id === planIntent);
        if (planObj) {
          // Show diet upsell; onAdd/onDecline handlers trigger the actual checkout
          setDietUpsellData({ planId: planIntent, planName: planObj.name, planPrice: planObj.price });
          return; // wait for modal choice
        }
        // Plans not loaded yet — proceed directly without upsell (fail-safe)
        await startMembershipCheckout({
          planId: planIntent, memberId: currentMemberId,
          originUrl, token: localStorage.getItem("memberToken"), includeDietPlan: false,
        });
        return;
      }

      // 3. Free first class (from homepage "Book First Class Free" or lead popup firstClass flow)
      if (offerIntent === "firstclass" || freeClass) {
        if (referralUpsellEnabled) {
          setStep(3);
        } else {
          navigate("/claim-free-class");
        }
        return;
      }

      // 4. Free 1-week elite pass (from lead popup free week flow)
      if (offerIntent === "freeweek" || claimedFreeWeek) {
        navigate("/claim-free-week");
        return;
      }

      // 5. Default — go to member dashboard, or back to where they came from
      // (e.g. /dashboard/diet-plan when registering from the public diet plan page)
      const redirectAfterReg = sessionStorage.getItem("nbc_redirect_after_login");
      if (redirectAfterReg && redirectAfterReg.startsWith('/')) {
        sessionStorage.removeItem("nbc_redirect_after_login");
        navigate(redirectAfterReg);
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error("Failed to submit waiver");
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    sigCanvas.current?.clear();
  };

  const updateReferral = (index, field, value) => {
    setReferrals(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const handleReferralSubmit = async () => {
    for (let i = 0; i < 3; i++) {
      const r = referrals[i];
      if (!r.first_name.trim() || !r.last_name.trim() || !r.phone.trim()) {
        toast.error(`Please complete all fields for Friend ${i + 1}`);
        return;
      }
    }
    setReferralLoading(true);
    try {
      const token = localStorage.getItem("memberToken");
      await axios.post(
        `${API}/referral-upsell`,
        {
          member_id: memberId,
          referrals: referrals.map(r => ({
            first_name: r.first_name.trim(),
            last_name: r.last_name.trim(),
            phone: r.phone.trim(),
          })),
          inviter_name: `${formData.first_name} ${formData.last_name}`.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Your friends have been notified — enjoy your free week!");
      navigate("/claim-free-week");
    } catch {
      toast.error("Couldn't send the referrals, but you're all set!");
      navigate("/claim-free-class");
    } finally {
      setReferralLoading(false);
    }
  };

  const handleReferralDecline = () => {
    navigate("/claim-free-class");
  };

  const waiverText = `
    LIABILITY WAIVER AND RELEASE FORM
    
    I understand that participating in boxing training and fitness activities involves inherent risks, 
    including but not limited to physical injury, disability, and death. I voluntarily assume all risks 
    associated with my participation in any and all activities at Neighborhood Boxing Club.
    
    I release, waive, and discharge Neighborhood Boxing Club, its owners, trainers, employees, and agents 
    from any and all liability for any injury, damage, or loss that I may sustain as a result of participating 
    in any activities at the facility.
    
    I certify that I am physically fit and have no medical condition that would prevent my participation 
    in boxing training and fitness activities. I agree to follow all safety rules and instructions provided 
    by the staff.
    
    By signing below, I acknowledge that I have read this waiver, understand its contents, and agree to 
    its terms.
  `;

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-12">
      {/* Diet plan upsell — shown after waiver, before homepage-plan checkout */}
      {dietUpsellData && (
        <DietPlanUpsellModal
          planName={dietUpsellData.planName}
          planPrice={dietUpsellData.planPrice}
          onAdd={async () => {
            const { planId } = dietUpsellData;
            setDietUpsellData(null);
            try {
              await startMembershipCheckout({
                planId, memberId, originUrl: window.location.origin,
                token: localStorage.getItem("memberToken"), includeDietPlan: true,
              });
            } catch { toast.error("Failed to start checkout"); }
          }}
          onDecline={async () => {
            const { planId } = dietUpsellData;
            setDietUpsellData(null);
            try {
              await startMembershipCheckout({
                planId, memberId, originUrl: window.location.origin,
                token: localStorage.getItem("memberToken"), includeDietPlan: false,
              });
            } catch { toast.error("Failed to start checkout"); }
          }}
          onClose={async () => {
            const { planId } = dietUpsellData;
            setDietUpsellData(null);
            try {
              await startMembershipCheckout({
                planId, memberId, originUrl: window.location.origin,
                token: localStorage.getItem("memberToken"), includeDietPlan: false,
              });
            } catch { toast.error("Failed to start checkout"); }
          }}
        />
      )}
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <img 
            src="/nbc_logo.jpg"
            alt="NBC"
            className="h-16 mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold uppercase">
            {step === 1 ? "Create Account" : step === 2 ? "Sign Waiver" : "Unlock Your Free Week"}
          </h1>
          <p className="text-white/60 mt-2">
            {step === 1 ? "Join the NBC Boxing family" : step === 2 ? "Required to complete registration" : "One step away from a full week on us"}
          </p>
          
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-[#FFB612] text-black' : 'bg-white/10 text-white/40'}`}>
              {step > 1 ? <Check className="w-5 h-5" /> : "1"}
            </div>
            <div className="w-12 h-0.5 bg-white/10">
              <div className={`h-full transition-all ${step > 1 ? 'w-full bg-[#FFB612]' : 'w-0'}`} />
            </div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-[#FFB612] text-black' : 'bg-white/10 text-white/40'}`}>
              {step > 2 ? <Check className="w-5 h-5" /> : "2"}
            </div>
            {referralUpsellEnabled && (offerIntent === "firstclass" || freeClass) && (
              <>
                <div className="w-12 h-0.5 bg-white/10">
                  <div className={`h-full transition-all ${step > 2 ? 'w-full bg-[#FFB612]' : 'w-0'}`} />
                </div>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 3 ? 'bg-[#FFB612] text-black' : 'bg-white/10 text-white/40'}`}>
                  3
                </div>
              </>
            )}
          </div>
        </div>

        {step === 1 && (
          <form onSubmit={handleRegister} className="bg-[#0A0A0A] border border-white/10 p-8 space-y-5">
            {/* Show offer badge if applicable */}
            {dollarOffer && (
              <div className="bg-[#FFB612]/10 border border-[#FFB612]/30 p-3 text-center mb-4">
                <p className="text-[#FFB612] font-bold text-sm uppercase">
                  $1 First Month Offer Applied!
                </p>
              </div>
            )}
            {claimedFreeWeek && !dollarOffer && (
              <div className="bg-green-500/10 border border-green-500/30 p-3 text-center mb-4">
                <p className="text-green-400 font-bold text-sm uppercase">
                  Free 1 Week Pass Applied!
                </p>
              </div>
            )}

            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="text"
                placeholder="First Name *"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="bg-[#050505] border-white/20 h-12 pl-12 text-white placeholder:text-white/40"
                data-testid="register-first-name-input"
                required
              />
            </div>

            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="text"
                placeholder="Last Name *"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="bg-[#050505] border-white/20 h-12 pl-12 text-white placeholder:text-white/40"
                data-testid="register-last-name-input"
                required
              />
            </div>

            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="tel"
                placeholder="Phone Number *"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-[#050505] border-white/20 h-12 pl-12 text-white placeholder:text-white/40"
                data-testid="register-phone-input"
                required
              />
            </div>

            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="email"
                placeholder="Email Address *"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-[#050505] border-white/20 h-12 pl-12 text-white placeholder:text-white/40"
                data-testid="register-email-input"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="password"
                placeholder="Password *"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="bg-[#050505] border-white/20 h-12 pl-12 text-white placeholder:text-white/40"
                data-testid="register-password-input"
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="password"
                placeholder="Confirm Password *"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="bg-[#050505] border-white/20 h-12 pl-12 text-white placeholder:text-white/40"
                data-testid="register-confirm-password-input"
              />
            </div>

            <div className="space-y-2">
              <div className="relative">
                <Gift className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  type="text"
                  placeholder="Referral Code (Optional)"
                  value={formData.referralCode}
                  onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })}
                  className="bg-[#050505] border-white/20 h-12 pl-12 text-white placeholder:text-white/40"
                  data-testid="register-referral-input"
                />
              </div>
              {referrerName && (
                <div className="bg-green-500/10 border border-green-500/30 p-3 text-sm">
                  <p className="text-green-400">
                    <Check className="w-4 h-4 inline mr-2" />
                    Referred by <span className="font-bold">{referrerName}</span>! They'll get a free month when you join.
                  </p>
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase tracking-wide h-12"
              data-testid="register-submit-btn"
            >
              {loading ? "Creating Account..." : "Continue"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>
        )}

        {step === 2 && (
          <div className="bg-[#0A0A0A] border border-white/10 p-8 space-y-6">

            {/* ID Required Alert */}
            <div className="flex items-start gap-3 border border-[#FFB612] bg-[#FFB612]/10 p-4">
              <div className="w-5 h-5 flex-shrink-0 mt-0.5 rounded-full bg-[#FFB612] flex items-center justify-center">
                <span className="text-black text-xs font-black">!</span>
              </div>
              <p className="text-white font-bold text-sm">
                ID Required: Bring a valid government-issued ID to your first class visit.
              </p>
            </div>

            {/* Member Name (read-only from Step 1) */}
            <div>
              <p className="text-xs text-white/60 uppercase tracking-wider mb-1">Full Name</p>
              <p className="text-xl font-bold text-[#FFB612] uppercase tracking-wide" data-testid="waiver-member-name">
                {formData.first_name} {formData.last_name}
              </p>
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-xs text-white uppercase tracking-wider mb-1" htmlFor="dob-input">
                Date of Birth *
              </label>
              <Input
                id="dob-input"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="bg-[#050505] border-white/20 h-12 text-white"
                data-testid="waiver-dob-input"
                required
              />
            </div>

            {/* Home Address */}
            <div>
              <label className="block text-xs text-white uppercase tracking-wider mb-1" htmlFor="address-input">
                Home Address *
              </label>
              <Input
                id="address-input"
                type="text"
                placeholder="123 Main St, City, State ZIP"
                value={homeAddress}
                onChange={(e) => setHomeAddress(e.target.value)}
                className="bg-[#050505] border-white/20 h-12 text-white placeholder:text-white/40"
                data-testid="waiver-address-input"
                required
              />
            </div>

            {/* Waiver Text */}
            <div className="bg-[#050505] border border-white/10 p-4 max-h-48 overflow-y-auto">
              <pre className="text-sm text-white/70 whitespace-pre-wrap font-sans">
                {waiverText}
              </pre>
            </div>

            {/* Agreement Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToWaiver}
                onChange={(e) => setAgreedToWaiver(e.target.checked)}
                className="mt-1 w-5 h-5 accent-[#FFB612]"
                data-testid="waiver-agree-checkbox"
              />
              <span className="text-sm text-white/70">
                I have read and agree to the terms of the liability waiver above.
              </span>
            </label>

            {/* Signature Pad */}
            <div>
              <p className="text-sm text-white/60 mb-2">Sign Below:</p>
              <div className="bg-white border-2 border-[#FFB612] relative">
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="black"
                  canvasProps={{
                    className: "w-full h-32",
                    style: { width: "100%", height: "128px" }
                  }}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={clearSignature}
                className="text-white/50 hover:text-white text-sm mt-2"
              >
                Clear Signature
              </Button>
            </div>

            <Button
              onClick={handleWaiverSubmit}
              disabled={loading || !agreedToWaiver}
              className="w-full bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase tracking-wide h-12"
              data-testid="waiver-submit-btn"
            >
              {loading ? "Submitting..." : dollarOffer ? "Sign & Proceed to Payment" : "Complete Registration"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 3: Referral Upsell — upgrade to Free 1-Week Elite Unlimited Pass */}
        {step === 3 && (
          <div className="bg-[#0A0A0A] border border-white/10 p-8 space-y-6">
            {/* Headline */}
            <div className="text-center">
              <div className="inline-block bg-[#FFB612] text-black text-xs font-black uppercase tracking-widest px-3 py-1 mb-4">
                Exclusive Upgrade
              </div>
              <h2 className="text-2xl font-black uppercase text-white leading-tight">
                Want a Full Week Free<br />Instead of Just One Class?
              </h2>
              <p className="text-white mt-3 text-sm leading-relaxed">
                Refer <span className="text-[#FFB612] font-bold">3 friends or family members</span> and we'll upgrade you from a single free class to an <span className="text-[#FFB612] font-bold">entire week of unlimited Elite classes</span> — completely free. Your friends get invited to their first class free too. Everyone wins.
              </p>
            </div>

            {/* Phone format instruction */}
            <div className="bg-[#FFB612]/10 border border-[#FFB612]/30 px-4 py-2.5">
              <p className="text-white text-xs">
                <span className="text-[#FFB612] font-bold">Phone format:</span> 10-digit US number — e.g. <span className="font-mono text-white">614-503-7888</span> or <span className="font-mono text-white">(614) 503-7888</span>
              </p>
            </div>

            {/* 3 Referral Inputs */}
            <div className="space-y-5">
              {referrals.map((ref, i) => {
                const phoneDigits = ref.phone.replace(/\D/g, '');
                const phoneValid = phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith('1'));
                const showPhoneError = ref.phone.trim().length > 0 && !phoneValid;
                return (
                <div key={i} className="border border-white/10 p-4 space-y-3">
                  <p className="text-xs text-[#FFB612] font-bold uppercase tracking-wider">Friend {i + 1}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      type="text"
                      placeholder="First Name *"
                      value={ref.first_name}
                      onChange={e => updateReferral(i, "first_name", e.target.value)}
                      className="bg-[#050505] border-white/20 text-white placeholder:text-white/40 h-11"
                      data-testid={`referral-${i}-first-name`}
                    />
                    <Input
                      type="text"
                      placeholder="Last Name *"
                      value={ref.last_name}
                      onChange={e => updateReferral(i, "last_name", e.target.value)}
                      className="bg-[#050505] border-white/20 text-white placeholder:text-white/40 h-11"
                      data-testid={`referral-${i}-last-name`}
                    />
                  </div>
                  <div>
                    <Input
                      type="tel"
                      placeholder="Phone Number * (e.g. 614-503-7888)"
                      value={ref.phone}
                      onChange={e => updateReferral(i, "phone", e.target.value)}
                      className={`bg-[#050505] h-11 text-white placeholder:text-white/40 ${showPhoneError ? 'border-red-500' : 'border-white/20'}`}
                      data-testid={`referral-${i}-phone`}
                    />
                    {showPhoneError && (
                      <p className="text-red-400 text-xs mt-1" data-testid={`referral-${i}-phone-error`}>
                        Enter a valid 10-digit US number (e.g. 614-503-7888)
                      </p>
                    )}
                  </div>
                </div>
                );
              })}
            </div>

            {/* CTA Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleReferralSubmit}
                disabled={referralLoading || !referrals.every(r => {
                  const d = r.phone.replace(/\D/g, '');
                  const validPhone = d.length === 10 || (d.length === 11 && d.startsWith('1'));
                  return r.first_name.trim() && r.last_name.trim() && r.phone.trim() && validPhone;
                })}
                className="w-full bg-[#FFB612] text-black hover:bg-[#E5A00D] font-black uppercase tracking-wide h-13 text-base disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="referral-submit-btn"
              >
                {referralLoading ? "Sending Invites..." : "Unlock My Free Week — Invite 3 Friends"}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <button
                onClick={handleReferralDecline}
                className="w-full border border-white/60 text-white font-bold uppercase tracking-wide text-sm py-3 hover:border-white hover:bg-white/10 transition-all"
                data-testid="referral-decline-btn"
              >
                No thanks, I'll keep my free class
              </button>
            </div>

            <p className="text-xs text-white/40 text-center">
              Your friends will receive an SMS invitation. By submitting, you confirm they agree to be contacted.
            </p>
          </div>
        )}

        <div className="text-center mt-6 space-y-4">
          <p className="text-white/60">
            Already have an account?{" "}
            <Link to="/login" className="text-[#FFB612] hover:underline">
              Sign In
            </Link>
          </p>
          <p className="text-white/40 text-sm">
            <Link to="/" className="hover:text-[#FFB612]">← Back to Website</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
